import { Controller } from "@hotwired/stimulus"

// Multiselect dropdown with chips display
// Usage:
// <div data-controller="multiselect" data-multiselect-options-value='["option1", "option2"]'>
//   <input type="hidden" data-multiselect-target="input" name="tags">
//   <div data-multiselect-target="display">...</div>
//   <div data-multiselect-target="dropdown">...</div>
// </div>

export default class extends Controller {
  static targets = ["input", "display", "dropdown", "search", "option", "chip", "placeholder"]
  static values = {
    options: Array,
    selected: { type: Array, default: [] },
    placeholder: { type: String, default: "Select options..." }
  }

  connect() {
    // Initialize selected values from hidden input if present
    if (this.hasInputTarget && this.inputTarget.value) {
      const values = this.inputTarget.value.split(',').filter(v => v.trim())
      this.selectedValue = values
    }
    this.render()

    // Close dropdown when clicking outside
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener('click', this.clickOutsideHandler)
  }

  disconnect() {
    document.removeEventListener('click', this.clickOutsideHandler)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    if (this.dropdownTarget.classList.contains('hidden')) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.dropdownTarget.classList.remove('hidden')
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  close() {
    this.dropdownTarget.classList.add('hidden')
    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
      this.filterOptions('')
    }
  }

  select(event) {
    const value = event.currentTarget.dataset.value
    if (!this.selectedValue.includes(value)) {
      this.selectedValue = [...this.selectedValue, value]
      this.updateInput()
      this.render()
    }
  }

  remove(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    this.selectedValue = this.selectedValue.filter(v => v !== value)
    this.updateInput()
    this.render()
  }

  toggleOption(event) {
    const value = event.currentTarget.dataset.value
    if (this.selectedValue.includes(value)) {
      this.selectedValue = this.selectedValue.filter(v => v !== value)
    } else {
      this.selectedValue = [...this.selectedValue, value]
    }
    this.updateInput()
    this.render()
  }

  updateInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = this.selectedValue.join(',')
      // Dispatch change event for form submission
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  filter(event) {
    const query = event.target.value.toLowerCase()
    this.filterOptions(query)
  }

  filterOptions(query) {
    this.optionTargets.forEach(option => {
      const text = option.textContent.toLowerCase()
      if (query === '' || text.includes(query)) {
        option.classList.remove('hidden')
      } else {
        option.classList.add('hidden')
      }
    })
  }

  render() {
    // Update chips display
    if (this.hasDisplayTarget) {
      const chipsHtml = this.selectedValue.map(value => `
        <span class="badge badge-primary gap-1" data-multiselect-target="chip">
          ${this.escapeHtml(value)}
          <button type="button"
                  class="btn btn-ghost btn-xs p-0 h-auto min-h-0"
                  data-action="click->multiselect#remove"
                  data-value="${this.escapeHtml(value)}">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3 h-3">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      `).join('')

      if (this.selectedValue.length > 0) {
        this.displayTarget.innerHTML = chipsHtml
        if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.add('hidden')
        }
      } else {
        this.displayTarget.innerHTML = ''
        if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.remove('hidden')
        }
      }
    }

    // Update option checkmarks
    this.optionTargets.forEach(option => {
      const value = option.dataset.value
      const checkmark = option.querySelector('[data-checkmark]')
      if (checkmark) {
        if (this.selectedValue.includes(value)) {
          checkmark.classList.remove('invisible')
        } else {
          checkmark.classList.add('invisible')
        }
      }
    })
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Auto-submit form when selection changes (optional)
  submitForm() {
    const form = this.element.closest('form')
    if (form) {
      form.requestSubmit()
    }
  }
}
